
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getTeamMemberLimit, calculateAdditionalMembersCost } from "@/utils/teamMemberLimits";

interface AdditionalMembersBillingProps {
  company: {
    id: string;
    plan: string;
    additional_team_members: number;
    additional_members_stripe_subscription_id: string | null;
  };
  currentMemberCount: number;
  onUpdate: () => void;
}

export const AdditionalMembersBilling = ({
  company,
  currentMemberCount,
  onUpdate,
}: AdditionalMembersBillingProps) => {
  const [additionalMembers, setAdditionalMembers] = useState(company.additional_team_members || 0);
  const [isLoading, setIsLoading] = useState(false);

  const baseLimit = getTeamMemberLimit(company.plan);
  const currentAdditionalMembers = Math.max(0, currentMemberCount - baseLimit);
  const monthlyCost = calculateAdditionalMembersCost(additionalMembers);

  const handleUpdateSubscription = async () => {
    if (additionalMembers < currentAdditionalMembers) {
      toast.error(`You cannot reduce below ${currentAdditionalMembers} additional members as you currently have them active`);
      return;
    }

    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please log in to continue");
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-team-member-subscription', {
        body: { additionalMembers },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw error;
      }

      toast.success("Additional team member subscription updated!");
      onUpdate();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error("Failed to update subscription");
    } finally {
      setIsLoading(false);
    }
  };

  if (company.plan === 'free') {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Team Members</CardTitle>
        <CardDescription>
          Your {company.plan} plan includes {baseLimit} team members. 
          Add more team members for $10/month each.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <span className="font-medium">Base limit:</span> {baseLimit} members
          </div>
          <div className="text-sm">
            <span className="font-medium">Current members:</span> {currentMemberCount}
          </div>
          {currentAdditionalMembers > 0 && (
            <Badge variant="secondary">
              {currentAdditionalMembers} additional active
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label htmlFor="additional-members" className="text-sm font-medium">
              Additional members to purchase:
            </label>
            <Input
              id="additional-members"
              type="number"
              min={currentAdditionalMembers}
              value={additionalMembers}
              onChange={(e) => setAdditionalMembers(parseInt(e.target.value) || 0)}
              className="mt-1"
            />
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">Monthly cost</div>
            <div className="text-lg font-semibold">${monthlyCost}</div>
          </div>
        </div>

        {company.additional_members_stripe_subscription_id && (
          <div className="text-sm text-muted-foreground">
            You have an active subscription for additional team members.
          </div>
        )}

        <Button 
          onClick={handleUpdateSubscription}
          disabled={isLoading || additionalMembers === company.additional_team_members}
          className="w-full"
        >
          {isLoading ? "Processing..." : "Update Subscription"}
        </Button>
      </CardContent>
    </Card>
  );
};
